import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui';
import { bookingApi } from '@/features/booking/api/booking.api';
import type { Booking } from '@/types';

interface BookingDisputeDialogProps {
  booking: Booking | null;
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

export function BookingDisputeDialog({ booking, open, onClose, onSubmitted }: BookingDisputeDialogProps) {
  const [reason, setReason] = useState('');

  const submit = useMutation({
    mutationFn: () => bookingApi.dispute(booking!.id, reason.trim()),
    onSuccess: () => {
      setReason('');
      onSubmitted();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Raise a dispute</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Funds will remain on hold until our team reviews your dispute.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe what went wrong with your trip so we can investigate.
        </Typography>
        <TextField
          label="Reason for dispute"
          multiline
          minRows={4}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Minimum 10 characters"
        />
      </DialogContent>
      <DialogActions>
        <Button intent="secondary-outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          intent="primary-dark"
          disabled={reason.trim().length < 10 || submit.isPending}
          onClick={() => submit.mutate()}
        >
          Submit dispute
        </Button>
      </DialogActions>
    </Dialog>
  );
}
